'use client';

import { chakra } from '@ironhack/design-system2/components/chakra';
import { Flex, type FlexProps } from '@ironhack/design-system2/components/flex';
import { Icon } from '@ironhack/design-system2/components/icon';
import { Text } from '@ironhack/design-system2/components/text';
import { Globe } from '@ironhack/design-system2/icons/globe';
import { useRouter } from 'next/navigation';
import { type ChangeEvent, type ReactElement } from 'react';
import { useMeasure } from 'react-use';

import {
  type FullParams,
  type PageType,
  getPageUrl,
} from '@/app/urls/url-mapping';
import { sanitizeAlternateParams } from '@/lib/utils/meta-tags';
import {
  type Language,
  getLanguageName,
  regionToLanguages,
} from '@/lib/utils/language';

type Props = {
  languageOptions?: Partial<Record<Language, string>>;
  page: PageType;
  params: FullParams;
} & FlexProps;

export const LanguageSelector = (props: Props): ReactElement | null => {
  const { languageOptions, page, params, ...flexProps } = props;
  const { language, region } = params;
  const router = useRouter();
  const languages = languageOptions
    ? (Object.keys(languageOptions) as Language[])
    : regionToLanguages[region].filter((lang) =>
        getPageUrl(page, { language: lang, region })
      );
  const [ref, { width, height, top, left }] = useMeasure();

  const handleLanguageSelected = (event: ChangeEvent<HTMLSelectElement>) => {
    const newLanguage = event.target.value as Language;
    const baseParams = { language: newLanguage, region };
    const pageParams = sanitizeAlternateParams(params);
    const newUrl = languageOptions
      ? languageOptions[newLanguage]
      : getPageUrl(page, { ...pageParams, ...baseParams }) ??
        getPageUrl('home', baseParams);
    if (newUrl) router.push(newUrl);
  };

  const size = {
    ...(height > 0 && { h: `${height}px` }),
    ...(width > 0 && { w: `${width}px` }),
  };
  const position = { top, left };

  // Omit the selector id there's only one language option
  if (languages.length === 1) return null;

  return (
    <Flex position="relative" {...size} {...flexProps}>
      <chakra.select
        cursor="pointer"
        onChange={handleLanguageSelected}
        opacity={0}
        pos="absolute"
        value={language}
        zIndex={1}
        {...size}
        {...position}
      >
        {languages.map((lang) => (
          <option key={lang} value={lang}>
            {getLanguageName(lang, 'en')}
          </option>
        ))}
      </chakra.select>
      <Flex
        alignItems="center"
        as="button"
        cursor="pointer"
        gap={0.5}
        h="min-content"
        ref={ref}
        type="button"
        w="min-content"
        {...flexProps}
      >
        <Icon as={Globe} boxSize="24px" />
        <Text textStyle="l" textTransform="uppercase">
          {language}
        </Text>
      </Flex>
    </Flex>
  );
};
